// app/frontend/components/inbox/MessageBubble.tsx

import { useMemo, useState } from 'react'
import { Languages, ChevronDown, ChevronUp, Loader2, Mail } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Avatar } from '@/components/ui/Avatar'
import { linkify } from '@/lib/linkify'
import { isHtmlContent, sanitizeMessageHtml, shouldShowUrlPreview } from '@/lib/messageContent'
import { RICH_PROSE_BASE } from '@/lib/richProseClasses'
import { formatTime } from '@/lib/dateUtils'
import { getLanguageFlag } from '@/lib/languages'
import { UrlPreview, useMessageUrls } from '@/components/inbox/UrlPreview'
import { MessageAttachment } from '@/components/inbox/MessageAttachment'
import type { Message, TranslationState } from '@/types/conversation'

interface MessageBubbleProps {
  message: Message
  isFromAgent: boolean
  translation?: TranslationState
  onTranslate?: (messageId: string) => void
  accountWebsite?: string | null
}

export function MessageBubble({
  message,
  isFromAgent,
  translation,
  onTranslate,
  accountWebsite
}: MessageBubbleProps) {
  const [showAgentTranslation, setShowAgentTranslation] = useState(false)

  // For visitor messages: use on-demand translation from TranslationState
  const hasOnDemandTranslation = Boolean(translation?.translated && !translation?.error)
  const showOriginal = translation?.showOriginal ?? false

  // For agent messages: use stored translation from message.translation
  const hasStoredTranslation = isFromAgent && Boolean(message.translation?.translated)
  const storedTranslation = message.translation

  // Display content logic: always show translated if available, otherwise original
  const displayContent = hasOnDemandTranslation ? translation?.translated : message.content

  const isViaEmail = message.metadata?.source_channel === "email"

  // Extract URL for preview. Suppress cards for our own / the account's own
  // domain (quoted notification links) — see shouldShowUrlPreview.
  const previewUrl = useMessageUrls(message.content)
  const showPreview = Boolean(previewUrl) && shouldShowUrlPreview(previewUrl!, accountWebsite)

  // DOMPurify is O(n) in content length — memoize per content change so
  // unrelated re-renders (scroll, sibling typing indicator, etc.) don't
  // re-sanitize every bubble in the list.
  //
  // Gated on isFromAgent: the backend Loofah scrub only runs for agent
  // messages (Message#sanitize_html_content), so visitor content carrying
  // HTML must NOT be trusted as rich on the way out. Plain-text render
  // path escapes via React, which is safe.
  const renderedHtml = useMemo(
    () => isFromAgent && displayContent && isHtmlContent(displayContent)
      ? sanitizeMessageHtml(displayContent)
      : null,
    [isFromAgent, displayContent]
  )

  // Flags
  const sourceFlag = translation?.sourceLanguage
    ? getLanguageFlag(translation.sourceLanguage)
    : null
  const targetFlag = storedTranslation?.target_language
    ? getLanguageFlag(storedTranslation.target_language)
    : null

  return (
    <div className={cn("flex gap-3 max-w-[80%]", isFromAgent && "ml-auto flex-row-reverse")}>
      <Avatar
        name={message.sender_name || undefined}
        src={message.sender_avatar_url || undefined}
        avatarType={message.sender_avatar_url ? 'custom' : 'initials'}
        size="sm"
        className="shrink-0"
      />
      <div className="flex flex-col gap-1 min-w-0">
        {/* Bubble with optional flag indicator - only show if there's content */}
        {displayContent && (
          <div className="flex items-end gap-1.5">
            {/* Flag for agent messages (left of bubble) */}
            {isFromAgent && hasStoredTranslation && targetFlag && (
              <span
                className="text-xs bg-muted/50 border border-border rounded-full px-1.5 py-0.5 flex items-center gap-0.5 mb-1 cursor-help"
                title={`Translated to ${storedTranslation?.target_language_name || storedTranslation?.target_language}`}
              >
                <span className="text-[10px]">{targetFlag}</span>
                <span className="text-[9px] text-muted-foreground uppercase">{storedTranslation?.target_language}</span>
              </span>
            )}
            <div className={cn(
              "rounded-2xl px-4 py-2 text-sm",
              isFromAgent ? "bg-primary text-primary-foreground" : "bg-muted"
            )}>
              {renderedHtml !== null ? (
                <div
                  className={cn(
                    'text-current break-words [overflow-wrap:anywhere]',
                    RICH_PROSE_BASE,
                    '[&_code]:bg-black/10 [&_blockquote]:border-current/40 [&_a]:text-current'
                  )}
                  dangerouslySetInnerHTML={{ __html: renderedHtml }}
                />
              ) : (
                <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{linkify(displayContent)}</p>
              )}
            </div>
            {/* Flag for visitor messages (right of bubble) */}
            {!isFromAgent && hasOnDemandTranslation && sourceFlag && (
              <span
                className="text-xs bg-muted/50 border border-border rounded-full px-1.5 py-0.5 flex items-center gap-0.5 mb-1 cursor-help"
                title={`Translated from ${translation?.sourceLanguageName || translation?.sourceLanguage}`}
              >
                <span className="text-[10px]">{sourceFlag}</span>
                <span className="text-[9px] text-muted-foreground uppercase">{translation?.sourceLanguage}</span>
              </span>
            )}
          </div>
        )}

        {/* URL Preview - shown below the message bubble */}
        {previewUrl && showPreview && <UrlPreview url={previewUrl} className={isFromAgent ? "ml-auto" : ""} />}

        {/* Attachment display */}
        {message.attachment && (
          <MessageAttachment
            attachment={message.attachment}
            isFromAgent={isFromAgent}
            className={isFromAgent ? "ml-auto" : ""}
          />
        )}

        {/* Loading indicator for translation in progress */}
        {!isFromAgent && translation?.loading && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" />
            Translating...
          </span>
        )}

        {/* Toggle to show/hide translated text for visitor messages */}
        {!isFromAgent && hasOnDemandTranslation && (
          <button
            onClick={() => onTranslate?.(message.id)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            <Languages className="size-3" />
            <span>
              {showOriginal
                ? 'Show translation'
                : `Translated from ${translation?.sourceLanguageName || translation?.sourceLanguage}`}
            </span>
            {showOriginal ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          </button>
        )}

        {/* Translation controls for agent messages with stored translation */}
        {isFromAgent && hasStoredTranslation && (
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => setShowAgentTranslation(!showAgentTranslation)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <Languages className="size-3" />
              <span>
                {showAgentTranslation
                  ? 'Hide translation'
                  : `Translated to ${storedTranslation?.target_language_name || storedTranslation?.target_language}`}
              </span>
              {showAgentTranslation ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
            </button>
          </div>
        )}

        {/* Show original text when translation is toggled (visitor messages) */}
        {!isFromAgent && hasOnDemandTranslation && showOriginal && (
          <div className="px-3 py-2 rounded-lg bg-muted/50 text-sm text-muted-foreground">
            <p className="text-xs font-medium mb-1">Original:</p>
            <p className="whitespace-pre-wrap break-words">{message.content}</p>
          </div>
        )}

        {/* Show translated text when expanded (agent messages) */}
        {isFromAgent && hasStoredTranslation && showAgentTranslation && (
          <div className="px-3 py-2 rounded-lg bg-primary/10 text-sm">
            <p className="text-xs font-medium mb-1 text-muted-foreground">Sent to visitor:</p>
            <p className="whitespace-pre-wrap break-words">{storedTranslation?.translated}</p>
          </div>
        )}

        <p className={cn(
          "text-xs text-muted-foreground",
          isFromAgent && "text-right"
        )}>
          {message.sender_name || 'Unknown'} · {formatTime(message.created_at)}
          {isViaEmail && (
            <span className="inline-flex items-center gap-0.5 ml-1.5 text-muted-foreground/70">
              <Mail className="size-3" />
              <span>via email</span>
            </span>
          )}
        </p>
      </div>
    </div>
  )
}

export default MessageBubble
